import * as React from 'react';
import { Alert, PageSection, Tab, Tabs, TabTitleText, Title } from '@patternfly/react-core';
import DashBoardPermissionInterface from '@app/Interface/DashboardPermissionInterface';
import { ReactTabulator } from 'react-tabulator';
import TabulatorCommonOptions from '@app/utils/TabulatorCommonOptions';
import ItemLoader from '@app/Components/ItemLoader';
import moment from "moment-timezone";
import CoopPriceProjectionInterface from '@app/Interface/CoopPriceProjectionInterface';
import MontanaPriceProjectionInterface from '@app/Interface/MontanaPriceProjectionInterface';
import CoopPriceProjectionHelper from '@app/Service/CoopPriceProjectionHelper';
import MontanaPriceProjectionHelper from '@app/Service/MontanaPriceProjectionHelper';
import { Link } from 'react-router-dom';
import TabulatorFormatters from '@app/utils/TabulatorFormatters';
import { useMediaQuery } from '@react-hook/media-query';

// Tabulator Themes
import "react-tabulator/lib/styles.css"; // default theme
import "react-tabulator/css/bootstrap/tabulator_bootstrap.min.css"; // use Theme(s)
import PageRouteInterface from '@app/Interface/PageRouteInterface';
import ErrorResponseInterface from '@app/Interface/ErrorResponseInterface';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';

interface MilkePricesProps {
    dashPermissions: DashBoardPermissionInterface;
    coopPageRoute: PageRouteInterface;
    montanaPageRoute: PageRouteInterface;
    isLoading: boolean;
    coopProjections: CoopPriceProjectionInterface[] | ErrorResponseInterface;
    montanaProjections: MontanaPriceProjectionInterface[] | ErrorResponseInterface;
}

const MilkPrices: React.FC<MilkePricesProps> = ({ dashPermissions, coopPageRoute, montanaPageRoute, isLoading, coopProjections, montanaProjections }) => {

    // in order to get tabulator to work with moment-timezone
    // we need to add moment to the global namespace.
    // see: https://stackoverflow.com/a/54135475/8038146
    window.moment = moment;

    const coopTableRef = React.useCallback(node => { return {current: node} }, []);
    const montanaTableRef = React.useCallback(node => { return {current: node} }, []);

    let defaultTabKey = 0;

    if (dashPermissions.viewCoop === false) {
        defaultTabKey = 1;
    }

    const isBigWindow = useMediaQuery('only screen and (min-width: 800px)');
    const maxWidth = isBigWindow ? "false" : 200;

    const tabulatorCoopColumns = [
        { title: "Date", field: "MonthYearDisplay", frozen: true, width: 85, resizable: false },
        { title: "CL I", field: "ClassI", minWidth: 40, maxWidth: maxWidth, formatter: 'money', formatterParams: { precision: 2 }, resizable: false },
        { title: "CL II", field: "ClassII", minWidth: 40, maxWidth: maxWidth, formatter: 'money', formatterParams: { precision: 2 }, resizable: false },
        { title: "CL III", field: "ClassIII", minWidth: 40, maxWidth: maxWidth, formatter: 'money', formatterParams: { precision: 2 }, resizable: false },
        { title: "CL IV", field: "ClassIV", minWidth: 40, maxWidth: maxWidth, formatter: 'money', formatterParams: { precision: 2 }, resizable: false },
        { title: "Mover", field: "ClassIM", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "Fat", field: "BFat", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "Pro", field: "Protein", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "OS", field: "OSol", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "NDA PPD", field: "PriceCWT", minWidth: 40, maxWidth: maxWidth, formatter: 'money', formatterParams: { precision: 2 }, resizable: false },
        { title: "NDA Price @ Standard", field: "BlendValue", minWidth: 40, maxWidth: maxWidth, formatter: 'money', formatterParams: { precision: 2 }, resizable: false },
        { title: "NDA Price @ Test", field: "FMMOPrice", minWidth: 40, maxWidth: maxWidth, formatter: 'money', formatterParams: { precision: 2 }, resizable: false },
    ];

    const tabulatorMontanaColumns = [
        { title: "Month", field: "MonthYearDisplay", frozen: true, width: 85, resizable: false },
        { title: "CL I", field: "Price", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "Skim", field: "Skim", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "BF", field: "BF", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "Quota", field: "Quota", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "Skim-QT", field: "Skim_Quota", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "BF-QT", field: "BF_Quota", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "Excess", field: "Excess", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "Skim-EX", field: "Skim_Excess", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "BF-EX", field: "BF_Excess", minWidth: 40, maxWidth: maxWidth, resizable: false },
    ];


    const [coopRows, setCoopRows] = React.useState<CoopPriceProjectionInterface[]>([]);

    const [montanaRows, setMontanaRows] = React.useState<MontanaPriceProjectionInterface[]>([]);

    const [activeTabKey, setActiveTabKey] = React.useState(defaultTabKey);

    const recordsToDisplay = Number(process.env.DASHBOARD_MILK_PRICES_TO_DISPLAY) ?? 5;

    const monthModifierFilter = Number(process.env.DASHBOARD_MILK_PRICES_MONTH_MODIFIER) ?? -1;

    const tabClickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>, tabIndex: string | number) => {

        if (typeof tabIndex === "number") {
            setActiveTabKey(tabIndex);
        }

    }

    // get COOP price projections
    React.useEffect(() => {

        if (dashPermissions.viewCoop && Array.isArray(coopProjections)) {

            const filteredRows = CoopPriceProjectionHelper.getRowsForDashboard(coopProjections, recordsToDisplay, monthModifierFilter);

            setCoopRows(filteredRows);

        }

    }, [recordsToDisplay, monthModifierFilter, coopProjections, dashPermissions.viewCoop]);

    // get Montana price projections
    React.useEffect(() => {

        if (dashPermissions.viewMontana && Array.isArray(montanaProjections)) {

            const filteredRows = MontanaPriceProjectionHelper.getRowsForDashboard(montanaProjections, recordsToDisplay, monthModifierFilter);

            setMontanaRows(filteredRows);

        }

    }, [dashPermissions.viewMontana, recordsToDisplay, monthModifierFilter, montanaProjections]);

    const coopTabulatorOptions = {
        headerSort: false, //disable header sort for all columns
        rowFormatter: TabulatorFormatters.coopRowFormatter, // future dates have red text

    };

    const montanaTabulatorOptions = {
        headerSort: false, //disable header sort for all columns
        rowFormatter: TabulatorFormatters.montanaRowFormatter, // future dates have red text
    };

    const missingDataAlert = (
        <Alert
            variant="warning"
            title="No Data"
            aria-live="polite"
            role="alert"
        >
            <p>No pricing data was found.</p>
        </Alert>
    );

    return (
        <PageSection>
            <Title headingLevel={'h2'} className="pf-u-mb-md">Milk Prices</Title>
            <Tabs mountOnEnter activeKey={activeTabKey} onSelect={tabClickHandler} isBox>
                <Tab eventKey={0} title={<TabTitleText>COOP</TabTitleText>} isHidden={!dashPermissions.viewCoop}>
                    <div id="coop-prices" className="pf-u-background-color-100 pf-u-p-md">
                        <div className="pf-u-my-md pf-u-font-weight-bold">
                            <em>
                                Please note, although the current month price is shown in black, it is an estimate and not finalized until the final price is announced.
                            </em>
                        </div>
                        {isLoading
                            ? <ItemLoader />
                            : coopRows.length === 0
                                ? missingDataAlert
                                : <SCTabulator
                                    tableRef={coopTableRef}
                                    data={coopRows}
                                    columns={tabulatorCoopColumns}
                                    layout={isBigWindow ? "fitColumns" : "fitData"}
                                    options={{
                                        ...options('nda-milk-pricing-coop-sc-no-move', true, false), ...coopTabulatorOptions
                                    }}
                                />
                        }
                        <div className="pf-u-mt-md">
                            {coopPageRoute.id > 0 && <Link to={{ pathname: coopPageRoute.path }}>Full report link</Link>}
                        </div>
                    </div>
                </Tab>
                <Tab eventKey={1} title={<TabTitleText>Montana</TabTitleText>} isHidden={!dashPermissions.viewMontana}>
                    <div id={'montana-prices'} className="pf-u-background-color-100 pf-u-p-md">
                        <div className="pf-u-my-md pf-u-font-weight-bold">
                            <em>
                                Please note, although the current month price is shown in black, it is an estimate and not finalized until the final price is announced.
                            </em>
                        </div>
                        {isLoading
                            ? <ItemLoader />
                            : montanaRows.length === 0
                                ? missingDataAlert
                                : <SCTabulator
                                    tableRef={montanaTableRef}
                                    data={montanaRows}
                                    columns={tabulatorMontanaColumns}
                                    layout={isBigWindow ? "fitColumns" : "fitData"}
                                    options={{
                                        ...options('nda-milk-pricing-montana-sc-no-move', true, false), ...montanaTabulatorOptions
                                    }}
                                />
                        }

                        <div className="pf-u-mt-md">
                            {montanaPageRoute.id > 0 && <Link to={{ pathname: montanaPageRoute.path }}>Full report link</Link>}
                        </div>
                    </div>
                </Tab>
            </Tabs>
        </PageSection>
    );

}

export default MilkPrices;